import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Box } from '@mui/material';
import { I18N_KEYS } from '@/language/keys';
import { FlexCol } from '@/UI/styles';
import { useCompositorView, useNodeData } from '@/components/Recipe/FlowContext';
import { LockAspectRatioButton } from '@/components/Common/LockAspectRatioButton';
import { useIsHovered } from '@/hooks/useIsHovered';
import { AppCheckbox } from '@/UI/AppCheckbox/AppCheckbox';
import { FF_COMP_TIMELINE_DISPLAY } from '@/consts/featureFlags';
import AppDivider from '@/UI/AppDivider/AppDivider';
import { NumberInput } from './NumberInput';
import { LayerPropertyPanelField } from './LayerPropertyPanelField';
import { DurationInput } from './Components/DurationInput';
import type { NodeId } from 'web';
import type { CompositorNodeV3 } from '@/types/nodes/compositor';

const DEFAULT_DURATION = 5;
const DEFAULT_FPS = 24;

export const CanvasLayerProperties = ({ nodeId }: { nodeId: NodeId }) => {
  const { t } = useTranslation();
  const { isHovered: isHovering, ...elementProps } = useIsHovered();
  const [isAspectRatioLocked, setIsAspectRatioLocked] = useState(true);
  const { stage, duration, fps, isTimelineEnabled } = useNodeData<CompositorNodeV3>(nodeId).data;
  const compositorView = useCompositorView(nodeId);

  const ff_isTimelinePhase1Enabled = useFeatureFlagEnabled(FF_COMP_TIMELINE_DISPLAY);

  const handleStageWidthChange = useCallback(
    (value: number) => {
      const newValue = Math.max(value, 1);
      const newStageHeight = isAspectRatioLocked ? (newValue / stage.width) * stage.height : stage.height;
      compositorView.setStageDimensions(newValue, newStageHeight);
    },
    [isAspectRatioLocked, stage.width, stage.height, compositorView],
  );

  const handleStageHeightChange = useCallback(
    (value: number) => {
      const newValue = Math.max(value, 1);
      const newStageWidth = isAspectRatioLocked ? (newValue / stage.height) * stage.width : stage.width;
      compositorView.setStageDimensions(newStageWidth, newValue);
    },
    [isAspectRatioLocked, stage.height, stage.width, compositorView],
  );

  const handleTimelineToggle = useCallback(
    (checked: boolean) => {
      compositorView.setIsCompositorTimelineEnabled(checked);
    },
    [compositorView],
  );

  const handleDurationChange = useCallback(
    (value: number) => {
      compositorView.setCompositorDuration(value);
    },
    [compositorView],
  );

  const handleFpsChange = useCallback(
    (value: number) => {
      compositorView.setCompositorFps(value);
    },
    [compositorView],
  );

  return (
    <FlexCol {...elementProps}>
      <Box sx={{ py: 2 }}>
        <LayerPropertyPanelField
          key="canvas-dimensions"
          label={t(I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.DIMENSIONS)}
        >
          <NumberInput value={stage.width} onSubmit={handleStageWidthChange} disabled={false} prefix="W" decimals={0} />
          <NumberInput
            value={stage.height}
            onSubmit={handleStageHeightChange}
            disabled={false}
            prefix="H"
            decimals={0}
          />
          <motion.div animate={{ opacity: isHovering || isAspectRatioLocked ? 1 : 0 }}>
            <LockAspectRatioButton isLocked={isAspectRatioLocked} onChange={setIsAspectRatioLocked} />
          </motion.div>
        </LayerPropertyPanelField>
      </Box>
      <AppDivider />
      <FlexCol sx={{ gap: 1, pt: 2 }}>
        {ff_isTimelinePhase1Enabled && (
          <LayerPropertyPanelField key="canvas-timeline-toggle">
            <AppCheckbox
              size="small"
              label={t(I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.TIMELINE)}
              checked={isTimelineEnabled}
              onChange={(_e, checked) => handleTimelineToggle(checked)}
              disabled={isTimelineEnabled && compositorView.isVideoInputConnected()}
            />
          </LayerPropertyPanelField>
        )}
        {ff_isTimelinePhase1Enabled && isTimelineEnabled && (
          <>
            <LayerPropertyPanelField
              key="canvas-duration"
              label={t(I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.DURATION)}
            >
              <DurationInput
                secDuration={duration ?? DEFAULT_DURATION}
                fps={fps ?? DEFAULT_FPS}
                onChange={handleDurationChange}
              />
            </LayerPropertyPanelField>
            {ff_isTimelinePhase1Enabled && (
              <LayerPropertyPanelField key="canvas-fps" label={t(I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.FPS)}>
                <NumberInput
                  value={fps ?? DEFAULT_FPS}
                  onSubmit={handleFpsChange}
                  disabled={false}
                  decimals={0}
                  blurOnEnter
                />
              </LayerPropertyPanelField>
            )}
          </>
        )}
        {!ff_isTimelinePhase1Enabled && isTimelineEnabled && duration !== undefined && (
          <LayerPropertyPanelField key="canvas-duration" label={t(I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.DURATION)}>
            <NumberInput
              value={duration ?? DEFAULT_DURATION}
              onSubmit={handleDurationChange}
              disabled={false}
              decimals={0}
              unit="s"
            />
          </LayerPropertyPanelField>
        )}
      </FlexCol>
    </FlexCol>
  );
};
